import { returnError400WithEmptyPayload, returnNoErrorWithPayload, UNKNOWN_USER_DISPLAYNAME_PLACEHOLDER_DTO, UNKNOWN_GROUP_GROUPNAME_PLACEHOLDER_DTO, } from './model/model';
import { REGEX_VALID_CUSTOMFIELD_ID } from './model/dto-validators';
import { REGEX_VALID_ACCOUNT_ID_OR_GROUP_ID } from './storage-service-helper';
import { sanitizeUserInput_contextId, sanitizeUserInput_optionId } from './util';
import { MAX_PERSISTABLE_GROUPIDS_IN_GROUP_INDEX_PER_CHUNK } from './limits';
const FIELDTYPE_MULTISELECT = 'com.atlassian.jira.plugin.system.customfieldtypes:multiselect';
const FIELDTYPE_RADIOBUTTONS = 'com.atlassian.jira.plugin.system.customfieldtypes:radiobuttons';
const FIELDTYPE_CASCADINGSELECT = 'com.atlassian.jira.plugin.system.customfieldtypes:cascadingselect';
const FIELDTYPE_MULTICHECKBOXES = 'com.atlassian.jira.plugin.system.customfieldtypes:multicheckboxes';
const FIELDTYPE_SINGLESELECT = 'com.atlassian.jira.plugin.system.customfieldtypes:select';
const COMPATIBLE_FIELD_TYPES = [FIELDTYPE_MULTISELECT, FIELDTYPE_RADIOBUTTONS, FIELDTYPE_CASCADINGSELECT, FIELDTYPE_MULTICHECKBOXES, FIELDTYPE_SINGLESELECT];
export const INCOMPATIBLE_FIELD_TYPE_SHORT = 'incompatible';
export const transformCustomFieldTypeToShort = (fieldType) => {
    if (fieldType) {
        return fieldType.replace('com.atlassian.jira.plugin.system.customfieldtypes:', '');
    }
    return INCOMPATIBLE_FIELD_TYPE_SHORT;
};
export const isFieldTypeWithMultipleDefaultValues = (fieldTypeShortOrLong) => {
    return (fieldTypeShortOrLong === FIELDTYPE_MULTISELECT ||
        fieldTypeShortOrLong === transformCustomFieldTypeToShort(FIELDTYPE_MULTISELECT) ||
        fieldTypeShortOrLong === FIELDTYPE_MULTICHECKBOXES ||
        fieldTypeShortOrLong === transformCustomFieldTypeToShort(FIELDTYPE_MULTICHECKBOXES));
};
export const isFieldTypeWithSingleDefaultValue = (fieldTypeShortOrLong) => {
    return (fieldTypeShortOrLong === FIELDTYPE_SINGLESELECT ||
        fieldTypeShortOrLong === transformCustomFieldTypeToShort(FIELDTYPE_SINGLESELECT) ||
        fieldTypeShortOrLong === FIELDTYPE_RADIOBUTTONS ||
        fieldTypeShortOrLong === transformCustomFieldTypeToShort(FIELDTYPE_RADIOBUTTONS));
};
export const isFieldTypeWithCascadingDefaultValue = (fieldTypeShortOrLong) => {
    return fieldTypeShortOrLong === FIELDTYPE_CASCADINGSELECT || fieldTypeShortOrLong === transformCustomFieldTypeToShort(FIELDTYPE_CASCADINGSELECT);
};
export const transformJiraRestResponse = async (restResponse, errorIdentifier) => {
    const errorOrPayload = {};
    if (restResponse.status === 204) {
        errorOrPayload.hasError = false;
        errorOrPayload.payload = {};
        return errorOrPayload;
    }
    else {
        if (restResponse.status !== 200 && restResponse.status !== 201) {
            errorOrPayload.errorMessage = `${restResponse.statusText ? restResponse.statusText : 'error'}`;
            errorOrPayload.errorId = errorIdentifier;
            errorOrPayload.statusCode = restResponse.status;
            errorOrPayload.hasError = true;
            if (typeof restResponse.json === 'function') {
                try {
                    errorOrPayload.payload = await restResponse.json();
                }
                catch (err) {
                    errorOrPayload.errorMessage = `Failed to parse response JSON (JSN001) - status ${restResponse.status} - statusLine ${restResponse.statusText}`;
                    errorOrPayload.errorId = errorIdentifier;
                    errorOrPayload.statusCode = restResponse.status;
                    errorOrPayload.hasError = true;
                    return errorOrPayload;
                }
                // SEE REST_RESPONSE_BAD_REQUEST in transformer-service-errors.ts
                if (errorOrPayload.payload && errorOrPayload.payload['errorMessages'] && errorOrPayload.payload['errorMessages'][0]) {
                    errorOrPayload.errorMessage = (restResponse.statusText ? restResponse.statusText : 'error') + ' - ' + errorOrPayload.payload['errorMessages'][0];
                    errorOrPayload.errorId = errorIdentifier;
                }
                // SEE REST_RESPONSE_ERROR_ALLOWLIST_ACTIVE_FORBIDDEN in transformer-service-errors.ts
                if (errorOrPayload.payload && errorOrPayload.payload['message']) {
                    errorOrPayload.errorMessage = (restResponse.statusText ? restResponse.statusText : 'error') + ' - ' + errorOrPayload.payload['message'];
                    errorOrPayload.errorId = errorIdentifier;
                }
            }
            else {
                errorOrPayload.payload = {};
            }
        }
        else {
            errorOrPayload.hasError = false;
            let jsonBody = {};
            if (typeof restResponse.json === 'function') {
                try {
                    jsonBody = await restResponse.json();
                }
                catch (err) {
                    errorOrPayload.errorMessage = `Failed to parse response JSON (JSN002) - status ${restResponse.status} - statusLine ${restResponse.statusText}`;
                    errorOrPayload.errorId = errorIdentifier;
                    errorOrPayload.statusCode = restResponse.status;
                    errorOrPayload.hasError = true;
                    return errorOrPayload;
                }
            }
            errorOrPayload.payload = jsonBody;
        }
    }
    return errorOrPayload;
};
export const isValidCustomfieldId = (fieldId) => {
    const isValid = fieldId !== null && fieldId !== undefined && `${fieldId}`.match(REGEX_VALID_CUSTOMFIELD_ID) !== null;
    return {
        isValid: isValid,
        value: isValid ? fieldId : '-1',
    };
};
export const isValidContextId = (contextId) => {
    const isValid = contextId !== null && contextId !== undefined && `${contextId}`.match(/^[0-9]+$/) !== null; // isNumber(contextId);
    return {
        isValid: isValid,
        value: isValid ? parseInt(`${contextId}`, 10) : -1,
    };
};
export const isValidUserAccountId = (accountId) => {
    const isValid = accountId !== null && accountId !== undefined && `${accountId}`.match(REGEX_VALID_ACCOUNT_ID_OR_GROUP_ID) !== null;
    return {
        isValid: isValid,
        value: isValid ? accountId : 'null',
    };
};
export const filterAndPaginateDTOCustomField = (fields, pagination) => {
    const paginationResult = {
        startAt: pagination && pagination.startAt ? pagination.startAt : 0,
        maxResults: pagination && pagination.maxResults ? (pagination.maxResults > 50 ? 50 : pagination.maxResults) : 50,
        isLast: true,
        total: 0,
        values: [],
    };
    if (fields) {
        //
        // Step 1: Filter
        //
        let filteredFields = [];
        if (pagination !== null && pagination.currentFilter) {
            filteredFields = fields.filter((f) => f.fieldName && f.fieldName.toLocaleLowerCase().includes(`${pagination.currentFilter}`.toLocaleLowerCase()));
        }
        else {
            filteredFields = fields;
        }
        //
        // Step 2: Paginate
        //
        paginationResult.total = filteredFields.length;
        paginationResult.isLast = paginationResult.total <= paginationResult.startAt + paginationResult.maxResults;
        paginationResult.values = filteredFields.slice(paginationResult.startAt, paginationResult.startAt + paginationResult.maxResults);
    }
    return paginationResult;
};
export const filterAndPaginateRestFieldDetails = (fields, pagination) => {
    const paginationResult = {
        startAt: pagination && pagination.startAt ? pagination.startAt : 0,
        maxResults: pagination && pagination.maxResults ? (pagination.maxResults > 50 ? 50 : pagination.maxResults) : 50,
        isLast: true,
        total: 0,
        values: [],
    };
    if (fields) {
        //
        // Step 1: Filter
        //
        let filteredFields = [];
        if (pagination !== null && pagination.currentFilter) {
            filteredFields = fields.filter((f) => f.name && f.name.toLocaleLowerCase().includes(`${pagination.currentFilter}`.toLocaleLowerCase()));
        }
        else {
            filteredFields = fields;
        }
        //
        // Step 2: Paginate
        //
        paginationResult.total = filteredFields.length;
        paginationResult.isLast = paginationResult.total <= paginationResult.startAt + paginationResult.maxResults;
        paginationResult.values = filteredFields.slice(paginationResult.startAt, paginationResult.startAt + paginationResult.maxResults);
    }
    return paginationResult;
};
export const paginateContextResultWithoutFilter = (contexts, pagination) => {
    const paginationResult = {
        startAt: pagination && pagination.startAt ? pagination.startAt : 0,
        maxResults: pagination && pagination.maxResults ? (pagination.maxResults > 50 ? 50 : pagination.maxResults) : 50,
        isLast: true,
        total: 0,
        values: [],
    };
    if (contexts) {
        //
        // Paginate
        //
        paginationResult.total = contexts.length;
        paginationResult.isLast = paginationResult.total <= paginationResult.startAt + paginationResult.maxResults;
        paginationResult.values = contexts.slice(paginationResult.startAt, paginationResult.startAt + paginationResult.maxResults);
    }
    return paginationResult;
};
export const filter_enforceOnlyAllowedFieldTypes = (field) => {
    return field && field.schema && field.schema.custom && COMPATIBLE_FIELD_TYPES.includes(field.schema.custom) ? true : false;
};
export const filter_enforceFieldIsNotProjectScopeField = (field) => {
    return !('scope' in field) || (field.scope && field.scope.type !== 'PROJECT');
};
export const transformRestFieldDetailsToDTOsWithPermissions = (restFields, permissions, isAdminView) => {
    const permissionsNullSafe = permissions ? permissions.filter((p) => p !== null) : [];
    // console.log(JSON.stringify(restFields, null, 2));
    if (!restFields) {
        return [];
    }
    const result = [];
    for (const compatibleField of restFields) {
        if (compatibleField && compatibleField.id) {
            const userHasContextPermissionOnField = permissionsNullSafe.findIndex((p) => p.fieldId === compatibleField.id && p.contextId !== null) !== -1;
            const userHasGlobalPermissionOnField = permissionsNullSafe.findIndex((p) => p.fieldId === compatibleField.id && p.contextId === null) !== -1;
            if (userHasContextPermissionOnField || userHasGlobalPermissionOnField || isAdminView) {
                result.push({
                    fieldId: compatibleField.id,
                    fieldName: compatibleField.name,
                    fieldType: transformCustomFieldTypeToShort(compatibleField.schema.custom),
                    fieldDescription: compatibleField.description,
                    fieldGlobalPermission: isAdminView ? true : userHasGlobalPermissionOnField,
                    fieldIsLocked: compatibleField.isLocked,
                    fieldIsCompatible: filter_enforceOnlyAllowedFieldTypes(compatibleField),
                });
            }
        }
    }
    return result;
};
export const getFieldIdsUserHasAtLeastContextPermissionsFor = (permissions) => {
    const permissionsNullSafe = permissions ? permissions.filter((p) => p !== null) : [];
    const idsOnly = permissionsNullSafe.map((p) => p.fieldId);
    const uniqueIds = Array.from(new Set(idsOnly));
    return uniqueIds;
};
export const getContextIdsUserHasPermissionsFor = (permissions, fieldId) => {
    const permissionsNullSafe = permissions ? permissions.filter((p) => p !== null) : [];
    const idsOnly = permissionsNullSafe
        .filter((p) => p.fieldId === fieldId)
        .map((p) => p.contextId)
        .filter((p) => p !== null);
    const uniqueIds = Array.from(new Set(idsOnly));
    return uniqueIds;
};
export const _transformSingleRestContextToDTO = (context) => {
    return {
        contextId: `${context.id}`,
        contextName: context.name,
        contextDescription: context.description,
        issueTypes: [],
        projects: [],
        isGlobalContext: context.isGlobalContext,
        isAnyIssueType: context.isAnyIssueType,
    };
};
export const transformRestContextsToDTOsAndEnforceUserPermissions = (fieldId, restContexts, permissions) => {
    const result = [];
    // GUARD
    if (!permissions) {
        return result;
    }
    const userOrGroupPermissionsNullSafe = permissions.userOrGroupPermissions ? permissions.userOrGroupPermissions.filter((x) => x !== null) : [];
    //
    // ADMIN
    //
    if (permissions.isAdmin) {
        for (const context of restContexts) {
            result.push(_transformSingleRestContextToDTO(context));
        }
        return result;
    }
    //
    // USER WITH PERMISSIONS
    //
    const alreadyAddedContextIds = [];
    for (const permission of userOrGroupPermissionsNullSafe) {
        if (permission.fieldId === fieldId && permission.contextId === null) {
            //
            // GLOBAL PERMISSION
            //
            for (const context of restContexts) {
                if (!alreadyAddedContextIds.includes(context.id)) {
                    result.push(_transformSingleRestContextToDTO(context));
                    alreadyAddedContextIds.push(context.id);
                }
            }
        }
        else if (permission.fieldId === fieldId && permission.contextId !== null) {
            //
            // CONTEXT PERMISSION
            //
            const context = restContexts.find((c) => `${c.id}` === `${permission.contextId}`);
            if (context && !alreadyAddedContextIds.includes(context.id)) {
                result.push(_transformSingleRestContextToDTO(context));
                alreadyAddedContextIds.push(context.id);
            }
        }
    }
    return result;
};
export const transformRestUserSearchResultToDTOs = (userPickerResult) => {
    const ret = [];
    if (userPickerResult && userPickerResult.total > 0) {
        return userPickerResult.users
            .filter((u) => u.accountType === 'atlassian') // FIXME: filter for users only! 2023-02-16 seems to be the current way to do so ...
            .map((u) => ({
            avatarUrl: u.avatarUrl ? u.avatarUrl : null,
            accountId: u.accountId,
            displayName: u.displayName,
        }));
    }
    return ret;
};
export const transformRestGroupSearchResultToDTOs = (groupPickerResult) => {
    const ret = [];
    if (groupPickerResult && groupPickerResult.total > 0) {
        return groupPickerResult.groups.map((g) => ({
            groupId: g.groupId,
            groupName: g.name,
        }));
    }
    return ret;
};
export const transformRestBulkUserSearchResultToDTOs = (userBulkResult) => {
    const ret = [];
    if (userBulkResult && userBulkResult.total > 0) {
        return userBulkResult.values
            .filter((uObj) => uObj !== null) // NOTE: When the API does not find a user for given accountId it returns NULL instead of an user object. Big facepalm here!
            .map((u) => ({
            avatarUrl: u.avatarUrls ? u.avatarUrls['32x32'] : null,
            accountId: u.accountId,
            displayName: u.displayName,
        }));
    }
    return ret;
};
export const transformRestBulkGroupSearchResultToDTOs = (groupBulkResult) => {
    const ret = [];
    if (groupBulkResult && groupBulkResult.total > 0) {
        return groupBulkResult.values.map((g) => ({
            groupId: g.groupId,
            groupName: g.name,
        }));
    }
    return ret;
};
export const transformGroupIdList_To_RestBulkGroupSearchResultItem__WithUnknownGroupNamePlaceholder = (groupIds) => {
    const result = [];
    if (!groupIds || groupIds.length === 0) {
        return result;
    }
    for (const groupId of groupIds) {
        result.push({
            groupId: groupId,
            name: UNKNOWN_GROUP_GROUPNAME_PLACEHOLDER_DTO,
        });
    }
    return result;
};
export const transformUserAccountIdList_To_RestBulkUserSearchResultItem__WithUnknownDisplayNamePlaceholder = (userAccountIds) => {
    const result = [];
    if (!userAccountIds || userAccountIds.length === 0) {
        return result;
    }
    for (const userId of userAccountIds) {
        result.push({
            accountId: userId,
            active: false,
            timeZone: null,
            displayName: UNKNOWN_USER_DISPLAYNAME_PLACEHOLDER_DTO,
            avatarUrls: null,
        });
    }
    return result;
};
/*
 See unit test - basically we have these lists:
 list1  = [ { name: "UNKNOWN", id: 1}, { name: "UNKNOWN", id: 2} ]
 list2  = [ { name: "foo", id: 1} ]
 result = [ { name: "foo", id: 1}, { name: "UNKNOWN", id: 2} ]
 NOTE: The list1 is the one that holds the truth. It holds the list of groupIds.
       The list2 comes from the JIRA API and when Jira does not know the group for groupId it returns nothing and even strips the groupId from the lis
       therefore we enrich the list1 with the groupNames from list2
 */
export const mergeLeft_GroupName_of_RestBulkGroupSearchResultItem = (list1, list2) => {
    const result = [];
    if (!list1 || list1.length === 0) {
        return result;
    }
    let nullSafeList2 = [];
    if (list2 && list2.length > 0) {
        nullSafeList2 = list2;
    }
    for (const list1item of list1) {
        if (list1item && list1item.groupId) {
            const list2FoundItem = nullSafeList2.find((list2item) => list2item && list2item.groupId === list1item.groupId);
            result.push({
                groupId: list1item.groupId,
                name: list2FoundItem ? list2FoundItem.name : list1item.name,
            });
        }
    }
    return result;
};
/*
 See unit test - basically we have these lists:
 list1  = [ { name: "UNKNOWN", id: 1}, { name: "UNKNOWN", id: 2} ]
 list2  = [ { name: "foo", id: 1} ]
 result = [ { name: "foo", id: 1}, { name: "UNKNOWN", id: 2} ]
 */
export const mergeLeft_DisplayNameAndAvatarUrl_of_RestBulkUserSearchResultItem = (list1, list2) => {
    const result = [];
    if (!list1 || list1.length === 0) {
        return result;
    }
    let nullSafeList2 = [];
    if (list2 && list2.length > 0) {
        nullSafeList2 = list2;
    }
    for (const list1item of list1) {
        if (list1item && list1item.accountId) {
            // Note for unit test: Jira API returns null entries for whole user when accountId is unknown!
            const list2FoundItem = nullSafeList2.find((list2item) => list2item && list2item.accountId === list1item.accountId);
            result.push({
                accountId: list1item.accountId,
                displayName: list2FoundItem ? list2FoundItem.displayName : list1item.displayName,
                avatarUrls: list2FoundItem ? list2FoundItem.avatarUrls : list1item.avatarUrls,
                active: list2FoundItem ? list2FoundItem.active : list1item.active,
                timeZone: list2FoundItem ? list2FoundItem.timeZone : list1item.timeZone,
            });
        }
    }
    return result;
};
export const transformRestDefaultValuesToDTOs = (result, fieldType) => {
    const ret = {};
    if (isFieldTypeWithSingleDefaultValue(fieldType)) {
        // VALID SINGLE OR RADIO
        // {"maxResults":50,"startAt":0,"total":1,"isLast":true,"values":[{"type":"option.single","contextId":"10165","optionId":"10039"}]}
        if (result && result.total > 0 && result.values && result.values[0]) {
            if (result.values[0].optionId) {
                ret.defaultOption = {
                    optionId: result.values[0].optionId,
                    optionValue: '...', // WILL BE DONE IN FRONTEND
                };
            }
        }
    }
    else if (isFieldTypeWithCascadingDefaultValue(fieldType)) {
        // VALID PARENT ONLY CASCDING (NO CHILD OPTION SET!)
        // {"maxResults":50,"startAt":0,"total":1,"isLast":true,"values":[{"type":"option.cascading","contextId":"10138","optionId":"18006"}]}
        // VALID PARENT WITH CHILD CASCADING
        // {"maxResults":50,"startAt":0,"total":1,"isLast":true,"values":[{"type":"option.cascading","contextId":"10138","optionId":"18006","cascadingOptionId":"18007"}]}
        if (result && result.total > 0 && result.values && result.values[0]) {
            // PARENT
            if (result.values[0].optionId) {
                ret.defaultOption = {
                    optionId: result.values[0].optionId,
                    optionValue: '...', // WILL BE DONE IN FRONTEND
                };
            }
            // OPTIONAL CHILD
            if (result.values[0].cascadingOptionId) {
                ret.defaultChildOption = {
                    optionId: result.values[0].cascadingOptionId,
                    optionValue: '...', // WILL BE DONE IN FRONTEND
                };
            }
        }
    }
    else if (isFieldTypeWithMultipleDefaultValues(fieldType)) {
        // VALID MULTICHECKBOXES AND MULTISELECT
        // {"maxResults":50,"startAt":0,"total":1,"isLast":true,"values":[{"type":"option.multiple","contextId":"10166","optionIds":["10040","18008"]}]}
        if (result && result.total > 0 && result.values && result.values[0]) {
            if (result.values[0].optionIds) {
                ret.defaultOptions = result.values[0].optionIds.map((oId) => ({
                    optionId: oId,
                    optionValue: '...', // WILL BE DONE IN FRONTEND
                }));
            }
        }
    }
    return ret;
};
export const transformDTOSetDefaultOptionToRESTrepresentationForHttpPut = (dto, fieldTypeShort) => {
    if (isFieldTypeWithSingleDefaultValue(fieldTypeShort)) {
        if (dto.doFullDelete === true) {
            return {
                defaultValues: [
                    {
                        contextId: sanitizeUserInput_contextId(`${dto.contextId}`),
                        optionId: null,
                        type: 'option.single',
                    },
                ],
            };
        }
        else {
            return {
                defaultValues: [
                    {
                        contextId: sanitizeUserInput_contextId(`${dto.contextId}`),
                        optionId: dto.optionId === null ? null : sanitizeUserInput_optionId(`${dto.optionId}`),
                        type: 'option.single',
                    },
                ],
            };
        }
    }
    if (isFieldTypeWithCascadingDefaultValue(fieldTypeShort)) {
        if (dto.doFullDelete === true) {
            return {
                defaultValues: [
                    {
                        contextId: sanitizeUserInput_contextId(`${dto.contextId}`),
                        optionId: null,
                        cascadingOptionId: null,
                        type: 'option.cascading',
                    },
                ],
            };
        }
        else {
            const defObj = {
                defaultValues: [
                    {
                        contextId: sanitizeUserInput_contextId(`${dto.contextId}`),
                        optionId: dto.optionId === null ? null : sanitizeUserInput_optionId(`${dto.optionId}`),
                        cascadingOptionId: undefined,
                        type: 'option.cascading',
                    },
                ],
            };
            if (dto.childOptionId || dto.childOptionId === null) {
                // child option is optional
                defObj.defaultValues[0].cascadingOptionId = dto.childOptionId === null ? null : sanitizeUserInput_optionId(`${dto.childOptionId}`);
            }
            return defObj;
        }
    }
    if (isFieldTypeWithMultipleDefaultValues(fieldTypeShort)) {
        if (dto.doFullDelete === true) {
            return {
                defaultValues: [
                    {
                        contextId: sanitizeUserInput_contextId(`${dto.contextId}`),
                        optionIds: null,
                        type: 'option.multiple',
                    },
                ],
            };
        }
        else {
            return {
                defaultValues: [
                    {
                        contextId: sanitizeUserInput_contextId(`${dto.contextId}`),
                        optionIds: dto.optionIds ? dto.optionIds.map((oId) => sanitizeUserInput_optionId(`${oId}`)) : null,
                        type: 'option.multiple',
                    },
                ],
            };
        }
    }
};
export const error_invalidUserAccountId = () => {
    return {
        hasError: true,
        statusCode: 400,
        errorMessage: 'The accountId is empty or invalid.',
        errorId: 'INVLDACCNTD_233465',
        payload: {},
    };
};
/*
 USE LIKE SO:
 pageSliceOfList(myList, 1, 10); == FIRST PAGE
 pageSliceOfList(myList, 2, 10); == SECOND PAGE
 pageSliceOfList(myList, 3, 10); == THIRD PAGE
*/
export const pageSliceOfList = (listItems, page, maxPerPage) => {
    if (!listItems || listItems.length === 0) {
        return [];
    }
    return listItems.slice((page - 1) * maxPerPage, (page - 1) * maxPerPage + maxPerPage);
};
/*
 USE LIKE SO:
 fromToSliceOfListNullSafe(myList, 0, 10); == FIRST PAGE
 fromToSliceOfListNullSafe(myList, 10, 10); == SECOND PAGE
 fromToSliceOfListNullSafe(myList, 20, 10); == THIRD PAGE

 NULLSAFE - ALWAYS RETURNS AT LEAST AN EMPTY LIST!
*/
export const fromToSliceOfListNullSafe = (listItems, startAt, maxResults) => {
    if (!listItems || listItems.length === 0) {
        return [];
    }
    return listItems.slice(startAt, startAt + maxResults);
};
export const concatenate_paginatedRestResult = (errorAndPayloadExisting, errorAndPayloadNewValues) => {
    const errorAndPayloadResult = {
        errorMessage: null,
        errorId: null,
        hasError: false,
        statusCode: 200,
        payload: {
            isLast: true,
            maxResults: 0,
            startAt: 0,
            total: 0,
            values: [],
        },
    };
    errorAndPayloadResult.hasError = errorAndPayloadNewValues.hasError;
    errorAndPayloadResult.errorId = errorAndPayloadNewValues.errorId;
    errorAndPayloadResult.errorMessage = errorAndPayloadNewValues.errorMessage;
    errorAndPayloadResult.statusCode = errorAndPayloadNewValues.statusCode;
    if (!errorAndPayloadNewValues.hasError) {
        errorAndPayloadResult.payload.values = errorAndPayloadExisting.payload.values.concat(errorAndPayloadNewValues.payload.values);
        errorAndPayloadResult.payload.isLast = errorAndPayloadNewValues.payload.isLast;
        errorAndPayloadResult.payload.maxResults = errorAndPayloadExisting.payload.maxResults + errorAndPayloadNewValues.payload.maxResults;
        errorAndPayloadResult.payload.startAt = errorAndPayloadExisting.payload.startAt;
        errorAndPayloadResult.payload.total = errorAndPayloadExisting.payload.total + errorAndPayloadNewValues.payload.total;
    }
    return errorAndPayloadResult;
};
export const doesUserHaveGlobalPermissionOnField = (permissions, fieldId) => {
    if (permissions && permissions.userOrGroupPermissions) {
        const hasAccessGlobalPermission = permissions.userOrGroupPermissions.findIndex((f) => f && f.fieldId === fieldId && f.contextId === null) !== -1;
        if (hasAccessGlobalPermission) {
            return true;
        }
        else {
            return false;
        }
    }
    else {
        return false;
    }
};
export const enforceUserHasAccessToFieldAndContext = (permissions, fieldId, contextId, errorId) => {
    const validCustomfieldIdCheck = isValidCustomfieldId(fieldId);
    if (!validCustomfieldIdCheck.isValid) {
        return returnError400WithEmptyPayload('invalid fieldId', errorId);
    }
    const fieldIdSafe = validCustomfieldIdCheck.value;
    const validContextIdCheck = isValidContextId(contextId);
    if (!validContextIdCheck.isValid) {
        return returnError400WithEmptyPayload('invalid contextId', errorId);
    }
    const contextIdSafe = validContextIdCheck.value;
    if (permissions.isAdmin) {
        return returnNoErrorWithPayload(null);
    }
    if (permissions && permissions.userOrGroupPermissions) {
        const hasAccessContextPermission = permissions.userOrGroupPermissions.findIndex((f) => f && f.fieldId === fieldIdSafe && f.contextId === contextIdSafe) !== -1;
        const hasAccessGlobalPermission = permissions.userOrGroupPermissions.findIndex((f) => f && f.fieldId === fieldIdSafe && f.contextId === null) !== -1;
        // console.log('permissions', permissions);
        if (hasAccessContextPermission || hasAccessGlobalPermission) {
            return returnNoErrorWithPayload(null);
        }
        else {
            return returnError400WithEmptyPayload('user has no access to customfield context', errorId);
        }
    }
    else {
        return returnError400WithEmptyPayload('no permissions for user or group', errorId);
    }
};
// Check if user has at least one context permission or global permission on given field
export const enforceUserHasAccessToFieldAndAtLeastOneContext = (permissions, fieldId, errorId) => {
    const validCustomfieldIdCheck = isValidCustomfieldId(fieldId);
    if (!validCustomfieldIdCheck.isValid) {
        return returnError400WithEmptyPayload('invalid fieldId', errorId);
    }
    const fieldIdSafe = validCustomfieldIdCheck.value;
    if (permissions.isAdmin) {
        return returnNoErrorWithPayload(null);
    }
    if (permissions && permissions.userOrGroupPermissions) {
        const hasAccessContextPermission = permissions.userOrGroupPermissions.findIndex((f) => f && f.fieldId === fieldIdSafe && f.contextId !== null) !== -1;
        const hasAccessGlobalPermission = permissions.userOrGroupPermissions.findIndex((f) => f && f.fieldId === fieldIdSafe && f.contextId === null) !== -1;
        // console.log('permissions', permissions);
        if (hasAccessContextPermission || hasAccessGlobalPermission) {
            return returnNoErrorWithPayload(null);
        }
        else {
            return returnError400WithEmptyPayload('user has no access to any customfield context', errorId);
        }
    }
    else {
        return returnError400WithEmptyPayload('no permissions for user or group', errorId);
    }
};
export const generateCreateObjectBasedOnParentOptionId_from_OptionDTOCreateOption = (createOptionObj) => {
    return createOptionObj.parentOptionId ? { ...createOptionObj.create, optionId: `${createOptionObj.parentOptionId}` } : createOptionObj.create;
};
export const generatelLoggedInUserGroupIds_from_DTOUserWithGroups = (dto) => {
    return dto.groups.items ? dto.groups.items.map((g) => g.groupId) : [];
};
export const getUserIdsNullSafe_from_StorageV1CustomfieldPermissions = (permissions) => {
    return permissions && permissions.userIds ? permissions.userIds : [];
};
export const getGroupIdsNullSafe_from_StorageV1CustomfieldPermissions = (permissions) => {
    return permissions && permissions.groupIds ? permissions.groupIds : [];
};
export const splitGroupIndexIntoTwoPieces_beforePersistingIt = (groupIndex) => {
    const chunkedGroupIndex = {
        part1: [],
        part2: [],
    };
    if (!groupIndex || groupIndex.length === 0) {
        return chunkedGroupIndex;
    }
    chunkedGroupIndex.part1 = groupIndex.slice(0, MAX_PERSISTABLE_GROUPIDS_IN_GROUP_INDEX_PER_CHUNK);
    chunkedGroupIndex.part2 = groupIndex.slice(MAX_PERSISTABLE_GROUPIDS_IN_GROUP_INDEX_PER_CHUNK, 2 * MAX_PERSISTABLE_GROUPIDS_IN_GROUP_INDEX_PER_CHUNK);
    return chunkedGroupIndex;
};
